'use client'

import { useT, useFormat, type TranslateFn } from '@/lib/i18n'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { SearchInput } from '@/components/ui/input'
import { DataSelect } from '@/components/ui/select'
import { TableCard, Table, THead, Th, TRow, Td } from '@/components/shared/table'
import { TableSkeleton, TableEmpty, TableError } from '@/components/shared/table-states'
import { Pagination } from '@/components/shared/pagination'
import { useAnomalyCasesSummaryV3, useAnomalyCasesListV3 } from '../anomalies-v3.hooks'
import {
  anomalyLevelLabels,
  ANOMALY_LEVEL_ORDER,
  mlLevelLabels,
  type AnomalyCaseRow,
  type AnomalyLevel,
} from '../anomaly-cases-v3.types'
import { ANOMALY_REASON_DICT } from '../anomaly-reasons'
import { getVisibleAnomalyReasons } from '../anomaly-reason-visibility'
import { AnomalyCaseDrawer } from './anomaly-case-drawer'
import { formatWeightDifference, WEIGHT_DELTA_HEADER_KEY, WEIGHT_DELTA_TOOLTIP_KEY } from '../weight-format'

/** Оговорка про anomaly score — та же, что на кросс-юнит экране. */
const ANOMALY_NOTE_KEY = 'analytics.cases.anomalyNote'

const COLS = 9

const LEVEL_STYLE: Record<AnomalyLevel, { text: string; box: string }> = {
  priority_review: { text: 'text-risk-high', box: 'bg-risk-high-bg border-risk-high' },
  high: { text: 'text-risk-high', box: 'bg-risk-high-bg border-risk-high' },
  elevated: { text: 'text-risk-medium', box: 'bg-risk-medium-bg border-risk-medium' },
  baseline: { text: 'text-risk-low', box: 'bg-risk-low-bg border-risk-low' },
}

function levelBadge(level: AnomalyLevel, t: TranslateFn) {
  const st = LEVEL_STYLE[level]
  return (
    <span className={cn('inline-flex items-center rounded-xs border px-1.5 py-0.5 text-[10px] font-medium', st.box, st.text)}>
      {anomalyLevelLabels(t)[level]}
    </span>
  )
}

function mlBadge(level: string, t: TranslateFn) {
  // Переменная класса переименована в `tone`: прежнее имя `t` затеняло переводчик.
  const tone = level === 'high' ? 'text-risk-high' : level === 'medium' ? 'text-risk-medium' : 'text-risk-low'
  const labels = mlLevelLabels(t)
  return <span className={cn('font-medium', tone)}>{labels[level as keyof typeof labels] ?? level}</span>
}

function fmt(n: number | null, d = 2): string {
  return n === null || n === undefined ? '—' : n.toFixed(d)
}

export interface CasesViewSeed {
  search?: string
  anomalyLevel?: string
  mlLevel?: string
  anomalyCohort?: string
}

/** Локальная дата в YYYY-MM-DD. Через toISOString нельзя: он переводит в UTC,
 *  и выбранное 1 января в нашем часовом поясе уехало бы на 31 декабря. */
function toDateParam(d: Date | undefined): string | undefined {
  if (!d) return undefined
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function AnomalyCasesView({
  scenarioId,
  seed,
  dateRange,
}: {
  scenarioId: number | 'all'
  seed?: CasesViewSeed
  /** Период по дате раскредитации импорта — из общего дейтпикера страницы. */
  dateRange?: { from?: Date; to?: Date }
}) {
  const t = useT()
  const f = useFormat()
  const scen = typeof scenarioId === 'number' && scenarioId >= 1 && scenarioId <= 5 ? scenarioId : undefined

  const [anomalyLevel, setAnomalyLevel] = useState(seed?.anomalyLevel ?? 'all')
  const [mlLevel, setMlLevel] = useState(seed?.mlLevel ?? 'all')
  // Когорта больше не имеет видимого контрола: вся пользовательская популяция
  // аномалий лежит в одной когорте `same_unit_history_wagon` (вторая,
  // `same_unit_no_history_wagon`, по построению существует только для 2022 —
  // года прогрева, который strict-prior контракт из витрины исключает).
  // Значение остаётся ВХОДОМ компонента: программный вызов с seed.anomalyCohort
  // фильтрует ровно как раньше, поле anomalyCohort в ответе API не трогается.
  // Аудит: ai_transit/v2/reports/ANOMALY_COHORT_UI_AUDIT_AND_CLEANUP.md
  const cohort = seed?.anomalyCohort ?? 'all'

  // Search lives in the URL (?q=) so a refresh or Back keeps the query. The
  // input stays local for responsiveness; the URL is rewritten only after the
  // debounce, otherwise every keystroke would land in history.
  const router = useRouter()
  const searchParams = useSearchParams()
  const urlQuery = searchParams.get('q') ?? ''
  const [search, setSearch] = useState(seed?.search ?? urlQuery)
  const debounced = useDebounce(search, 300)
  const lastWritten = useRef(urlQuery)

  useEffect(() => {
    if (debounced === lastWritten.current) return
    lastWritten.current = debounced
    const next = new URLSearchParams(Array.from(searchParams.entries()))
    if (debounced) next.set('q', debounced)
    else next.delete('q')
    const qs = next.toString()
    router.replace(qs ? `?${qs}` : '?', { scroll: false })
  }, [debounced, router, searchParams])

  // Back/forward changes the URL without touching our state — adopt it.
  useEffect(() => {
    if (urlQuery !== lastWritten.current) {
      lastWritten.current = urlQuery
      setSearch(urlQuery)
    }
  }, [urlQuery])

  const clearSearch = useCallback(() => setSearch(''), [])

  const [sortBy, setSortBy] = useState('anomalyScore')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [selected, setSelected] = useState<{ caseId: string; scenarioId: number } | null>(null)

  const filters = useMemo(
    () => ({
      scenario_id: scen,
      anomaly_level: anomalyLevel === 'all' ? undefined : anomalyLevel,
      ml_level: mlLevel === 'all' ? undefined : mlLevel,
      anomaly_cohort: cohort === 'all' ? undefined : cohort,
      search: debounced || undefined,
      date_from: toDateParam(dateRange?.from),
      date_to: toDateParam(dateRange?.to),
    }),
    [scen, anomalyLevel, mlLevel, cohort, debounced, dateRange?.from, dateRange?.to],
  )

  const summary = useAnomalyCasesSummaryV3(filters)
  const list = useAnomalyCasesListV3({ ...filters, page, sort_by: sortBy, sort_dir: sortDir, page_size: pageSize })

  // reset to page 1 whenever filters change
  const filterKey = JSON.stringify(filters) + pageSize
  const [lastKey, setLastKey] = useState(filterKey)
  if (filterKey !== lastKey) {
    setLastKey(filterKey)
    if (page !== 1) setPage(1)
  }

  const s = summary.data
  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasFilters = anomalyLevel !== 'all' || mlLevel !== 'all' || cohort !== 'all' || !!debounced

  const toggleSort = (key: string) => {
    if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else { setSortBy(key); setSortDir('desc') }
  }

  return (
    <div className="space-y-5">
      {/* Explanatory note. Model version moved to the drawer's technical block. */}
      <div className="flex items-start gap-2 rounded-sm2 border border-border-subtle bg-subtle px-3 py-2">
        <p className="text-[11px] leading-relaxed text-fg-secondary">{t(ANOMALY_NOTE_KEY)}</p>
      </div>

      {/* Summary cards (4-level) — counts are over the eligible population, so
          they always agree with the table total below. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Card label={t('analytics.cu.totalCases')} value={s ? f.int(s.totalCases) : '…'}
          hint={s ? t('analytics.cases.uniquePairs', { value: f.int(s.uniquePairs) }) : undefined} />
        {ANOMALY_LEVEL_ORDER.map((lvl) => (
          <Card key={lvl} label={anomalyLevelLabels(t)[lvl]}
            value={s ? f.int(s.byAnomalyLevel[lvl] ?? 0) : '…'}
            accent={LEVEL_STYLE[lvl].text} />
        ))}
      </div>

      {/* Filters. Пригодность маршрута сервис применяет одинаково к списку,
          сводке, поиску и фильтрам — переключать в UI нечего, поэтому прежних
          тумблеров «Скрыть маршруты ЕАЭС» и «Скрыть пустые маршруты» здесь нет. */}
      <div className="flex flex-wrap items-center gap-2 rounded-sm2 border border-border-subtle bg-surface p-3">
        <label className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
          {t('analytics.cases.anomalyLevelFilter')}
          <DataSelect value={anomalyLevel} onChange={(e) => setAnomalyLevel(e.target.value)} className="h-9 w-auto">
            <option value="all">{t('analytics.cu.allLevels')}</option>
            {ANOMALY_LEVEL_ORDER.map((l) => (
              <option key={l} value={l}>{anomalyLevelLabels(t)[l]}</option>
            ))}
          </DataSelect>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
          {t('analytics.cases.mlLevelFilter')}
          <DataSelect value={mlLevel} onChange={(e) => setMlLevel(e.target.value)} className="h-9 w-auto">
            <option value="all">{t('common.value.all')}</option>
            <option value="high">{t('common.risk.high')}</option>
            <option value="medium">{t('common.risk.medium')}</option>
            <option value="low">{t('common.risk.low')}</option>
          </DataSelect>
        </label>
        <div className="relative flex min-w-[260px] max-w-md flex-1 items-center">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('analytics.cases.searchPlaceholder')}
            aria-label={t('analytics.cases.searchAria')}
            className="pr-14"
            wrapperClassName="w-full"
          />
          {/* Spinner marks the gap between the last keystroke and the response. */}
          {search !== debounced || (search && list.isFetching) ? (
            <span className="absolute right-7 text-[10px] text-fg-tertiary" aria-hidden="true">…</span>
          ) : null}
          {search && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label={t('common.action.clearSearch')}
              className="absolute right-1.5 rounded-xs px-1.5 py-0.5 text-[12px] text-fg-tertiary hover:bg-subtle hover:text-fg-primary"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <TableCard
        header={
          <div className="flex items-center justify-between border-b border-border-subtle bg-subtle px-3 py-2 text-[11px] text-fg-tertiary">
            <span>{list.isLoading ? t('common.state.loading') : t('analytics.cases.totalCases', { count: total })}</span>
            {/* Model version deliberately not shown here — it lives in the drawer's
                collapsed technical details. */}
            <span>{t('analytics.cases.scenarios15')}</span>
          </div>
        }
        footer={
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            pageSizeOptions={[25, 50, 100, 200]}
            onPageSizeChange={setPageSize}
          />
        }
      >
        <Table className="text-[13px]">
          <THead>
            <Th>{t('analytics.cu.colCaseScenario')}</Th>
            <Th sortable sortDir={sortBy === 'anomalyScore' ? sortDir : null} onSort={() => toggleSort('anomalyScore')}>{t('analytics.cu.colAnomaly')}</Th>
            <Th>{t('analytics.cu.anomalyLevel')}</Th>
            <Th sortable sortDir={sortBy === 'mlScore' ? sortDir : null} onSort={() => toggleSort('mlScore')}>ML</Th>
            <Th>{t('common.field.cargo')}</Th>
            <Th>{t('common.field.route')}</Th>
            <Th sortable sortDir={sortBy === 'weightDiffPct' ? sortDir : null} onSort={() => toggleSort('weightDiffPct')}
              title={t(WEIGHT_DELTA_TOOLTIP_KEY)}>{t(WEIGHT_DELTA_HEADER_KEY)}</Th>
            <Th sortable sortDir={sortBy === 'daysBetween' ? sortDir : null} onSort={() => toggleSort('daysBetween')}>{t('analytics.col.days')}</Th>
            <Th>{t('analytics.cu.colWhyUnusual')}</Th>
          </THead>
          <tbody>
            {list.isLoading ? (
              <TableSkeleton cols={COLS} />
            ) : list.isError ? (
              <TableError cols={COLS} error={list.error} onRetry={() => list.refetch()} />
            ) : rows.length === 0 ? (
              debounced ? (
                // Пустой ответ на ЗАПРОС и пустой ответ на фильтры — разные
                // ситуации: в первой аналитику надо знать, по каким полям вообще
                // идёт поиск, и одним движением его сбросить.
                <TableEmpty
                  cols={COLS}
                  icon={MagnifyingGlass}
                  title={t('analytics.cases.searchEmptyTitle', { query: debounced })}
                  description={t('analytics.cases.searchEmptyHint')}
                  action={
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="rounded-xs border border-border-default px-2 py-1 text-[11px] text-fg-secondary hover:bg-subtle"
                    >
                      {t('common.action.clearSearch')}
                    </button>
                  }
                />
              ) : (
                <TableEmpty
                  cols={COLS}
                  icon={MagnifyingGlass}
                  title={t('analytics.cases.emptyTitle')}
                  description={
                    hasFilters
                      ? t('analytics.cases.emptyHint')
                      : t('analytics.cases.emptyPeriod')
                  }
                />
              )
            ) : (
              rows.map((r) => (
                <Row key={`${r.caseId}_${r.scenarioId}`} r={r} onClick={() => setSelected({ caseId: r.caseId, scenarioId: r.scenarioId })} />
              ))
            )}
          </tbody>
        </Table>
      </TableCard>

      <AnomalyCaseDrawer
        caseId={selected?.caseId ?? null}
        scenarioId={selected?.scenarioId ?? null}
        onClose={() => setSelected(null)}
      />
    </div>
  )

  function Row({ r, onClick }: { r: AnomalyCaseRow; onClick: () => void }) {
    // «—» and «0.0%» both used to hide several distinct situations; the shared
    // formatter resolves which one this row is and supplies the exact reason.
    const weight = formatWeightDifference(r, t)
    // Та же политика видимости, что и в карточке кейса: «Новый код груза на
    // вывоз» не повторяет «Новую комбинацию», когда обе причины сработали.
    // `r.anomalyReasons` не изменяется — фильтр возвращает новый массив.
    const visibleReasonCodes = getVisibleAnomalyReasons(r.anomalyReasons)
    const reasons = visibleReasonCodes.slice(0, 2).map((c) => ANOMALY_REASON_DICT[c]?.title ?? c)
    const more = visibleReasonCodes.length - reasons.length
    return (
      <TRow onClick={onClick}>
        <Td>
          <div className="font-mono text-[13px] text-fg-primary">#{r.caseId}</div>
          <div className="text-[10px] text-fg-tertiary">Сценарий {r.scenarioId}</div>
        </Td>
        <Td mono>{fmt(r.anomalyScore)}</Td>
        <Td>{levelBadge(r.anomalyLevel, t)}</Td>
        <Td mono>{fmt(r.mlScore)} · {mlBadge(r.mlLevel, t)}</Td>
        <Td className="text-fg-secondary">{r.cargo ?? '—'}</Td>
        <Td className="text-[11px] text-fg-secondary">{r.route ?? '—'}</Td>
        <Td mono title={weight.tooltip}>{weight.displayValue}</Td>
        <Td mono>{r.daysBetween ?? '—'}</Td>
        <Td>
          {reasons.length ? (
            <div className="flex flex-wrap gap-1">
              {/* параметр стрелки назван reason, а не t: в этом компоненте
                  t — переводчик из useT(), и прежнее имя его затеняло. */}
              {reasons.map((reason, i) => (
                <span key={i} className="rounded-xs bg-subtle px-1.5 py-0.5 text-[10px] text-fg-secondary">{reason}</span>
              ))}
              {more > 0 && <span className="text-[10px] text-fg-tertiary">+{more}</span>}
            </div>
          ) : (
            <span className="text-[10px] text-fg-tertiary">{t('analytics.cases.noLocalReason')}</span>
          )}
        </Td>
      </TRow>
    )
  }
}

function Card({ label, value, hint, accent, small }: { label: string; value: string; hint?: string; accent?: string; small?: boolean }) {
  return (
    <div className="flex min-h-20 flex-col items-start gap-1.5 rounded-sm2 border border-border-subtle bg-surface px-3 py-3 text-left transition-colors duration-150">
      <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-fg-tertiary">{label}</span>
      <div className="mt-auto">
        <div className={cn('font-mono font-medium leading-none tabular-nums', small ? 'text-[13px]' : 'text-[20px]', accent ?? 'text-fg-primary')}>
          {value}
        </div>
        {hint && <div className="mt-0.5 text-[10px] text-fg-tertiary">{hint}</div>}
      </div>
    </div>
  )
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}
